import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wind, Loader2, ArrowLeft, MailCheck } from 'lucide-react';
import { toast } from 'sonner';

const SIDE = 'https://images.pexels.com/photos/15969122/pexels-photo-15969122/free-photo-of-kitesurfers-in-sea.jpeg?auto=compress&cs=tinysrgb&w=1280';

export default function ForgotPassword() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/auth/forgot-password', {
        email, lang: i18n.language, origin: window.location.origin,
      });
      setSent(true);
    } catch (err) {
      toast.error(apiError(err, t('auth.forgot.failed')));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={SIDE} alt="Kitesurfing" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0.4), rgba(10,10,10,0.85))' }} />
        <div className="absolute bottom-10 left-10 right-10">
          <h2 className="font-display text-5xl">{t('auth.login.sideTitle1')}<br /><span className="text-gradient-blue">{t('auth.login.sideTitle2')}</span></h2>
          <p className="text-white/70 mt-3 max-w-sm">{t('auth.login.sideSub')}</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8"><Wind className="w-7 h-7 text-[#00B4D8]" /><span className="font-display text-2xl">KITEIQ</span></Link>

          {sent ? (
            <div data-testid="forgot-sent-state">
              <div className="w-12 h-12 rounded-2xl bg-[#2EE59D]/15 flex items-center justify-center mb-4">
                <MailCheck className="w-6 h-6 text-[#2EE59D]" />
              </div>
              <h1 className="text-2xl font-semibold">{t('auth.forgot.sent')}</h1>
              <p className="text-muted-foreground text-sm mt-2">{t('auth.forgot.sentDesc')}</p>
              <Link to="/login">
                <Button variant="outline" className="mt-6 bg-transparent border-white/15 hover:bg-white/10 rounded-xl" data-testid="forgot-back-login">
                  <ArrowLeft className="w-4 h-4 mr-2" /> {t('auth.forgot.backToLogin')}
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-semibold">{t('auth.forgot.title')}</h1>
              <p className="text-muted-foreground text-sm mt-1">{t('auth.forgot.subtitle')}</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="email">{t('auth.forgot.email')}</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('auth.forgot.emailPlaceholder')} className="mt-1.5 bg-[#12161B] border-white/15" data-testid="forgot-email" />
                </div>
                <Button type="submit" disabled={busy} className="w-full h-11 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl" data-testid="forgot-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('auth.forgot.submit')}
                </Button>
              </form>
              <p className="text-sm text-muted-foreground mt-6 text-center">
                <Link to="/login" className="text-[#48CAE4] hover:text-[#00B4D8] font-medium inline-flex items-center gap-1" data-testid="forgot-back-link">
                  <ArrowLeft className="w-3.5 h-3.5" /> {t('auth.forgot.backToLogin')}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
